import { useContext } from 'react'

import { useSelector } from 'react-redux'
import { Grid } from 'react-styled-flexboxgrid'

import AnalyticsPostCampaignReportMetric from '@components/Analytics/PostCampaignReport/Metric'
import {
  StyledCol,
  StyledRow,
} from '@components/Analytics/PostCampaignReport/Summary/Summary.styled'
import { TRIBE_PRICING_PAGE_METRIC_SUMMARY_UTM } from '@constants'
import { CampaignContext } from '@context/Campaign'
import { PCRChannels } from '@enums'
import { IApplicationState } from '@store'
import { isSupportedPCRMetricsChannel, selectSnapMetricsSummary } from '@store/postCampaignReport'
import { selectPcrMetricsSummary } from '@store/postCampaignReport/metrics/selectors'
import { selectXMetricsSummary } from '@store/postCampaignReport/xMetrics/selectors'

const externalLink = TRIBE_PRICING_PAGE_METRIC_SUMMARY_UTM

type Props = {
  channel: PCRChannels
  translationPrefix?: string
}

export const PostCampaignReportChannelSummary = ({
  translationPrefix = 'postCampaignReport.summary',
  channel,
}: Props) => {
  const {
    campaign: { id: campaignId, budget_total_currency: currency, is_price_hidden: isPriceHidden },
  } = useContext(CampaignContext)

  const metricSummary = useSelector((state: IApplicationState) => {
    if (isSupportedPCRMetricsChannel(channel)) {
      return selectPcrMetricsSummary(state, channel, campaignId)
    } else if (channel === PCRChannels.Snap) {
      return selectSnapMetricsSummary(state, campaignId)
    }
    return selectXMetricsSummary(state, campaignId)
  })

  return (
    <Grid fluid>
      <StyledRow>
        <StyledCol xs={6} lg={3} data-testid="total-posts-cell">
          <AnalyticsPostCampaignReportMetric
            title="totalPost"
            value={metricSummary.count}
            externalLink={externalLink}
            translationPrefix={translationPrefix}
          />
        </StyledCol>
        {!isPriceHidden && (
          <StyledCol xs={6} lg={3} data-testid="total-spend-cell">
            <AnalyticsPostCampaignReportMetric
              title="totalSpend"
              value={metricSummary.total_purchase_amount}
              externalLink={externalLink}
              currency={currency}
              translationPrefix={translationPrefix}
            />
          </StyledCol>
        )}
        <StyledCol xs={6} lg={3} data-testid="total-followers-cell">
          <AnalyticsPostCampaignReportMetric
            title="totalFollowers"
            value={metricSummary.total_followers}
            externalLink={externalLink}
            abbreviateValue
            translationPrefix={translationPrefix}
          />
        </StyledCol>

        <StyledCol xs={6} lg={3} data-testid="total-likes-cell">
          <AnalyticsPostCampaignReportMetric
            title="likes"
            value={metricSummary.likes}
            externalLink={externalLink}
            abbreviateValue
            translationPrefix={translationPrefix}
          />
        </StyledCol>
      </StyledRow>
      <StyledRow>
        <StyledCol xs={6} lg={3} data-testid="total-comments-cell">
          <AnalyticsPostCampaignReportMetric
            title="comments"
            value={metricSummary.comments}
            externalLink={externalLink}
            abbreviateValue
            translationPrefix={translationPrefix}
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-shares-cell">
          <AnalyticsPostCampaignReportMetric
            title="shares"
            value={metricSummary.shares}
            externalLink={externalLink}
            abbreviateValue
            translationPrefix={translationPrefix}
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-engagement-cell">
          <AnalyticsPostCampaignReportMetric
            title="engagement"
            value={metricSummary.engagement}
            externalLink={externalLink}
            abbreviateValue
            translationPrefix={translationPrefix}
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-engagement-rate-cell">
          <AnalyticsPostCampaignReportMetric
            title="engagementRate"
            value={metricSummary.engagement_pct}
            externalLink={externalLink}
            showPercentSign
            translationPrefix={translationPrefix}
          />
        </StyledCol>
      </StyledRow>
      <StyledRow>
        {!isPriceHidden && (
          <StyledCol xs={6} lg={3} data-testid="overall-cpe-cell">
            <AnalyticsPostCampaignReportMetric
              title="overallCostPerEngagement"
              value={metricSummary.cpe}
              externalLink={externalLink}
              currency={currency}
              translationPrefix={translationPrefix}
            />
          </StyledCol>
        )}
        <StyledCol xs={6} lg={3} data-testid="total-views-cell">
          <AnalyticsPostCampaignReportMetric
            title="totalViews"
            value={metricSummary.impressions}
            externalLink={externalLink}
            abbreviateValue
            translationPrefix={translationPrefix}
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-views-pct-cell">
          <AnalyticsPostCampaignReportMetric
            title="totalViewsRate"
            value={metricSummary.impressions_pct}
            externalLink={externalLink}
            showPercentSign
            translationPrefix={translationPrefix}
          />
        </StyledCol>
        {!isPriceHidden && (
          <StyledCol xs={6} lg={3} data-testid="overall-cpm-cell">
            <AnalyticsPostCampaignReportMetric
              title="overallCPM"
              value={metricSummary.cpm}
              externalLink={externalLink}
              currency={currency}
              translationPrefix={translationPrefix}
            />
          </StyledCol>
        )}
      </StyledRow>
      {!isPriceHidden && (
        <StyledRow>
          <StyledCol xs={6} lg={3} data-testid="overall-cpv-cell">
            <AnalyticsPostCampaignReportMetric
              title="overallCPV"
              value={metricSummary.cpv}
              externalLink={externalLink}
              currency={currency}
              translationPrefix={translationPrefix}
            />
          </StyledCol>
        </StyledRow>
      )}
    </Grid>
  )
}
